import type { HelpManual } from './guide.model';

/**
 * Getting around Spectrum: signing in, the frame every module shares (the
 * menu on the left with the module switcher at its top, the header with the
 * school, the year and your profile), and the Guide itself. Every app
 * carries it; its first topic holds the tour of the frame, which the Guide
 * offers once to a new user and on any screen with no tour of its own.
 *
 * Its topics belong to no one app's routes, so their `route` is blank except
 * for the Help centre, which every app serves at /help.
 */
export const SPECTRUM_BASICS_MANUAL: HelpManual = {
    module: 'spectrum',
    title: 'Getting around Spectrum',
    intro: 'Spectrum runs a school on one account: admissions, students, fees, staff, attendance, academics, transport, messages and more. '
        + 'Each of those is a **module**, and every module sits in the same frame: a **menu on the left** with the module switcher at its top, '
        + 'and a **header** with the school you are working in, the academic year where it matters, the **Guide** and your profile.\n\n'
        + 'This short manual covers signing in, moving between modules and schools, and getting help. Each module has a manual of its own, '
        + 'which opens from the **Guide** button in that module.',
    chapters: [
        { id: 'signing-in', title: 'Signing in', intro: 'Your account, the one-time code, an expired password, and choosing where to work.' },
        { id: 'basics', title: 'The frame every module shares', intro: 'The menu, the module switcher, the school and year, your profile, and the Guide.' },
    ],
    workflows: [
        {
            id: 'spectrum.first-day',
            title: 'Your first day on Spectrum',
            goal: 'Sign in, find your way round, and know where help is.',
            cadence: 'Once, when you are given an account',
            steps: [
                { text: 'Sign in with the email or mobile number your school registered, or with a one-time code sent to your mobile.', topic: 'spectrum.sign-in' },
                { text: 'If you work at more than one school, pick the one you are working at today.', topic: 'spectrum.sign-in' },
                { text: 'Take the tour of the frame: the menu, the module switcher, the school and your profile.', topic: 'spectrum.getting-around' },
                { text: 'Open **My Profile** from the menu under your name and check your details are right.', topic: 'spectrum.getting-around' },
                { text: 'Press **?** on any screen to open the Guide, and try **Show me around this screen**.', topic: 'spectrum.guide' },
            ],
        },
    ],
    glossary: [
        { term: 'Module', meaning: 'A group of screens for one area of work, such as Finance, Students or Transport. Switch modules from the Spectrum logo at the top of the menu.' },
        { term: 'School (institute)', meaning: 'The organisation whose data you are looking at. Shown at the left of the header. Staff who work at more than one school switch between them there.' },
        { term: 'Academic year (AY)', meaning: 'The school year a screen is showing, such as AY 2026-27. Modules that work year by year show a year pill in the header.' },
        { term: 'Guide', meaning: 'Spectrum\'s built-in help. Press ? on any screen, or click Guide in the header.' },
        { term: 'Tour', meaning: 'A short walk round a screen: each stop highlights one part and says what it does. Offered once on your first visit, and always available from the Guide.' },
        { term: 'Walkthrough', meaning: 'A job that crosses several screens, step by step. The Guide keeps your place and opens each screen for you.' },
        { term: 'One-time code (OTP)', meaning: 'A 6-digit code sent to your registered mobile, used to sign in instead of a password.' },
        { term: 'SmartParent', meaning: 'The app parents and students use. Its accounts are separate from staff accounts; parents do not sign in here.' },
    ],
    roles: [
        { term: 'Staff member', meaning: 'Sees the modules and screens their role opens. The menu shows only what you may use.' },
        { term: 'School administrator (super user)', meaning: 'Grants access to others in the Security module: which modules, which screens, and whether they may only view or also add, edit and delete.' },
        { term: 'Parent or guardian', meaning: 'Uses the SmartParent app, not these screens. A staff member who is also a parent sees their children listed separately when choosing where to sign in.' },
    ],
    topics: [
        {
            id: 'spectrum.sign-in',
            route: '',
            title: 'Signing in',
            chapter: 'signing-in',
            summary: 'One account opens every module your school has given you. Sign in with your email or mobile number and password, or with a one-time code sent to your mobile. If you work at more than one school, you choose which one after signing in.',
            audience: 'Everyone, at the start of each working session.',
            tasks: [
                {
                    title: 'Sign in with your password',
                    steps: [
                        'Open your school\'s Spectrum address in the browser.',
                        'Type your **Email or mobile** and your **Password**. The eye button shows the password as you type it.',
                        'Tick **Keep me signed in** on your own computer only.',
                        'Click **Sign in**.',
                        'If you have access at more than one school, pick the school under **Choose where to sign in** (double-click it to go straight in).',
                    ],
                    result: 'Spectrum opens the module you chose, at its first screen.',
                },
                {
                    title: 'Sign in with a one-time code',
                    when: 'When you have forgotten your password or are on a shared computer.',
                    steps: [
                        'On the sign-in screen, type your **Email or mobile** (leave the password empty).',
                        'Click **Sign in with a one-time code**. The code goes to the mobile registered on your account.',
                        'Type the 6-digit code from the SMS into the boxes. If it does not arrive, wait for the 30-second timer and click **Resend code**.',
                        'Click **Verify and sign in**.',
                    ],
                    result: 'You are signed in exactly as with a password.',
                },
                {
                    title: 'Set a new password when yours has expired',
                    steps: [
                        'Sign in as usual. Spectrum shows **Set a new password**.',
                        'Type the **New password** (at least 8 characters; the bars show how strong it is) and type it again in **Confirm new password**.',
                        'Click **Save and sign in**.',
                    ],
                    result: 'You are signed in, and the new password is the one to use from now on.',
                },
                {
                    title: 'Reset a forgotten password',
                    steps: [
                        'On the sign-in screen, click **Forgot password?**.',
                        'Under **Reset your password**, type your **Email or mobile** and click **Send code**.',
                        'Type the 6-digit code you were sent and click **Continue**.',
                        'Choose a new password of at least 8 characters, type it twice, and save it.',
                    ],
                    result: '**Password updated** — sign in with your new password.',
                },
            ],
            cautions: [
                'Sessions end after a period of inactivity. When that happens Spectrum says your session has expired and takes you back to sign in — anything not yet saved on the screen is lost, so save as you go.',
                'Every sign-in is recorded against your school\'s audit trail. Never share your account; ask your administrator for one of your own.',
            ],
            errors: [
                { message: 'Incorrect username or password.', meaning: 'The email or mobile and password do not match an active account.', fix: 'Check for typing mistakes and Caps Lock, or sign in with a one-time code. If it still fails, ask your school administrator to check your account is active.' },
                { message: 'Enter your email or mobile first, then request a code.', meaning: 'A one-time code was asked for with the Email or mobile box empty.', fix: 'Type your email or mobile, then click **Sign in with a one-time code** again.' },
                { message: 'That code is not right, or it has expired.', meaning: 'The 6 digits do not match the latest code sent, or it is too old.', fix: 'Check the latest SMS, or click **Resend code** and use the new one.' },
                { message: 'This account is not linked to any school.', meaning: 'The account signed in, but no school has given it access.', fix: 'Ask your school administrator to give your account access in the Security module.' },
                { message: 'Your session has expired. Redirecting to login…', meaning: 'You were inactive for too long, or signed out in another tab.', fix: 'Sign in again. Unsaved changes on the screen you were on are lost.' },
            ],
            faqs: [
                { q: 'I am a parent. Can I sign in here?', a: 'No — parents and students use the SmartParent app. The sign-in screen links to it.' },
                { q: 'I work at two schools. Do I need two accounts?', a: 'No. One account can have access at several schools. You choose the school after signing in, and can switch later from the header without signing in again.' },
                { q: 'Why didn\'t my one-time code arrive?', a: 'The code goes to the mobile number your school registered for you. Wait for the timer and click **Resend code**. If your number has changed, ask your school office to update it.' },
            ],
            related: ['spectrum.getting-around'],
            keywords: ['login', 'log in', 'sign on', 'password', 'otp', 'one time password', 'forgot password', 'reset password', 'expired', 'session'],
        },
        {
            id: 'spectrum.getting-around',
            route: '',
            title: 'Getting around Spectrum',
            chapter: 'basics',
            summary: 'Everything that is not the screen itself sits in two places. The **menu on the left** lists the open module\'s screens, with the module switcher at its top. The **header** shows the school you are working in, the academic year where the module works by year, the **Guide** and your profile.',
            audience: 'Everyone, on their first day.',
            tasks: [
                {
                    title: 'Open another module',
                    steps: [
                        'Click the **Spectrum logo** at the top of the menu (it shows the module you are in).',
                        'Type part of the module\'s name in **Search modules…**, or scroll the list.',
                        'Click the module. To keep this one open as well, hold **Ctrl** (**⌘** on a Mac) while clicking, or use the arrow button beside it to open it in a new tab.',
                    ],
                    result: 'The module opens at its first screen, still signed in.',
                },
                {
                    title: 'Open a screen from the menu',
                    steps: [
                        'Find the screen in the menu on the left; screens are grouped under headings.',
                        'Click it. The one you are on is highlighted.',
                        'To make more room, click **Collapse** at the foot of the menu: it shrinks to icons, and hovering over it opens it again.',
                    ],
                },
                {
                    title: 'Switch to another school',
                    when: 'When you work at more than one school.',
                    steps: [
                        'Click the switch button beside the school\'s name in the header.',
                        'Search for the school by name, or pick it from the list (**Current** marks the one you are in).',
                        'Click **Switch School**.',
                    ],
                    result: 'The module reloads for the school you chose.',
                },
                {
                    title: 'Change the academic year',
                    when: 'In modules that work year by year (Students, Academics, Finance, HR, Message Center, Documents).',
                    steps: [
                        'Click the year pill (**AY …**) in the header.',
                        'Pick the year.',
                    ],
                    result: 'The screens of that module now show the year you picked, until you change it.',
                },
                {
                    title: 'See your profile or sign out',
                    steps: [
                        'Click your name at the right of the header.',
                        'Click **My Profile** to see your own details, or **Logout** to end the session.',
                    ],
                },
            ],
            fields: [
                { term: 'Menu (left)', meaning: 'The open module\'s screens. It shows only the screens your role may use.' },
                { term: 'Spectrum logo', meaning: 'The module switcher: which module you are in, and every other module your school has given you.' },
                { term: 'School card (header, left)', meaning: 'The school whose data you are looking at, with its logo. Everything you save is saved to this school.' },
                { term: 'AY pill', meaning: 'The academic year the module\'s screens are showing.' },
                { term: 'Guide button', meaning: 'Help for the screen you are on. A dot on it means the screen has a tour you have not taken yet.' },
                { term: 'Your name (header, right)', meaning: 'Your profile menu: who you are signed in as, at which school, My Profile and Logout.' },
            ],
            tips: [
                'Hold Ctrl (⌘ on a Mac) when choosing a module to open it in a new browser tab and keep the one you are in.',
                'On a phone the menu is hidden; open it with the menu button.',
                'Screens remember nothing between schools: after switching school, check the filters on the screen before you act.',
            ],
            cautions: [
                'Check the school name in the header before saving anything. Staff with access to more than one school can otherwise record a fee, an admission or a mark against the wrong school.',
            ],
            faqs: [
                { q: 'A screen I used yesterday is missing from the menu.', a: 'Menus show only the screens your role opens. Ask your school administrator to check your role in the Security module.' },
                { q: 'Why don\'t I see the switch button beside the school name?', a: 'You only have access at one school, or you are in a support session, which is tied to one school.' },
                { q: 'Why is a menu item greyed out with "Soon"?', a: 'That screen is planned but not available yet.' },
            ],
            related: ['spectrum.guide', 'spectrum.sign-in'],
            tour: [
                { anchor: 'shell-module', title: 'Modules', body: 'This shows the module you are in. Click it to switch to another: Students, Finance, HR, Transport and the rest.' },
                { anchor: 'shell-nav', title: 'The module\'s screens', body: 'Every screen of this module, grouped under headings. You only see the screens your role may use.' },
                { anchor: 'shell-school', title: 'The school you\'re working in', body: 'Everything you see and save belongs to this school. If you work at more than one, switch here.' },
                { anchor: 'shell-year', title: 'The academic year', body: 'Modules that work year by year show the year here. Change it to look at another year.' },
                { anchor: 'shell-guide', title: 'The Guide', body: 'Press ? on any screen for step-by-step help, a tour of the screen, and the whole manual. It stays beside the screen while you work.' },
                { anchor: 'shell-profile', title: 'You', body: 'Your name and school, My Profile, and Logout.' },
            ],
            keywords: ['navigate', 'menu', 'sidebar', 'switch module', 'module switcher', 'switch school', 'change school', 'academic year', 'year', 'profile', 'logout', 'sign out'],
        },
        {
            id: 'spectrum.guide',
            route: '',
            title: 'Using the Guide',
            chapter: 'basics',
            summary: 'The Guide is help that sits beside the screen you are working on. **This screen** lists the jobs people do there as steps you can tick off; **Manual** searches every page of the module\'s manual. Tours walk you round a screen one part at a time.',
            audience: 'Everyone.',
            tasks: [
                {
                    title: 'Get help on the screen you are on',
                    steps: [
                        'Press **?** (or **F1**), or click **Guide** in the header.',
                        'Under **Things you can do here**, open the job you want to do.',
                        'Follow the steps on the screen, ticking each one as you go. The next step is highlighted.',
                        'Open **What you see on this screen**, **Messages you may see** or **People often ask** for more.',
                        'Press **Esc** or click the cross to close the Guide.',
                    ],
                    result: 'The Guide stays open beside the screen while you work; your ticks are kept for next time.',
                },
                {
                    title: 'Take a tour of a screen',
                    steps: [
                        'Open the Guide and click **Show me around this screen**.',
                        'Read each stop and click **Next** (or press the right arrow). **Back** goes back; **Skip** or **Esc** ends the tour.',
                    ],
                    result: 'Each part of the screen is highlighted in turn with a note on what it does.',
                },
                {
                    title: 'Search the manual',
                    steps: [
                        'Open the Guide and click the **Manual** tab.',
                        'Type what you are looking for — the name of a button, a column, a message you saw, or the job in your own words.',
                        'Click a result to open that screen with its help.',
                    ],
                },
                {
                    title: 'Follow a walkthrough',
                    when: 'For a job that crosses screens, such as starting a new academic year.',
                    steps: [
                        'In the Guide\'s **Manual** tab, or the Help centre\'s **Walkthroughs**, click **Start** on the one you need.',
                        'The Guide pins it at the top: each step says what to do and where. **Take me there** opens the screen.',
                        'Click **Done, next step** as you finish each one, or **Stop** to put it down.',
                    ],
                    result: 'Your place is kept, so you can stop and carry on later.',
                },
                {
                    title: 'Read or print the whole manual',
                    steps: [
                        'In the Guide, click **Read this page in the Help centre**, or **Open the Help centre** on the Manual tab.',
                        'Use the contents on the left to move between chapters, walkthroughs, roles and the glossary.',
                        'Click **Print manual** to print it or save it as a PDF.',
                    ],
                },
            ],
            fields: [
                { term: 'Dot on the Guide button', meaning: 'This screen has a tour you have not taken yet.' },
                { term: 'Ticked steps', meaning: 'Kept in this browser for your account, so the next time you open the job your place is there.' },
            ],
            tips: [
                'On a wide screen the Guide moves the screen aside instead of covering it, so you can read and click at once.',
                'Every tour is offered once. "Not now" does not mean never: the tour stays one click away in the Guide.',
            ],
            faqs: [
                { q: 'The Guide says there is no page for this screen yet.', a: 'Search the manual from the Manual tab, or take the tour of Spectrum. Tell your administrator which screen it was, so its page can be written.' },
                { q: 'Will the Guide change anything on my screen?', a: 'No. It only reads: it never saves, deletes or submits anything for you.' },
            ],
            related: ['spectrum.getting-around', 'spectrum.help-centre'],
            keywords: ['help', 'guide', 'manual', 'tour', 'walkthrough', 'documentation', 'how to', 'f1', 'question mark'],
        },
        {
            id: 'spectrum.help-centre',
            route: '/help',
            aliases: ['/help/:module'],
            title: 'Help centre',
            chapter: 'basics',
            summary: 'The whole manual of the module you are in, a chapter at a time: every screen, the walkthroughs that cross screens, who may do what, and a glossary. "Getting around Spectrum" is listed beside it.',
            audience: 'Anyone learning a module, and anyone training someone new.',
            reach: 'From the Guide: **Read this page in the Help centre**, or **Open the Help centre** on the Manual tab.',
            tasks: [
                {
                    title: 'Read a module\'s manual',
                    steps: [
                        'Pick the manual under **Manuals** on the left.',
                        'Click a chapter, or a card on the introduction page.',
                        'Click **Open the screen** beside a page to go straight to that screen with its Guide open.',
                    ],
                },
                {
                    title: 'Print the manual or save it as a PDF',
                    steps: [
                        'Click **Print manual** at the top right.',
                        'In the print dialog, pick your printer, or **Save as PDF**.',
                    ],
                    result: 'The whole manual prints as a document, a chapter to a page, without the menu and header around it.',
                },
            ],
            tips: [
                '**The whole manual on one page** in the contents is handy for searching with the browser\'s own Find (Ctrl F).',
            ],
            keywords: ['manual', 'documentation', 'user guide', 'training', 'print', 'pdf'],
        },
    ],
};
